import { EventEmitter } from 'events';

import { Logger } from './logger';
import { Session } from './session';
import { SyncClient } from 'twilio-sync';
import { isDeepEqual, parseAttributes } from './util';
import { validateTypesAsync, literal } from 'twilio-sdk-type-validator';

const log = Logger.scope('User');

interface UserState {
  identity: string;
  entityName: string;
  friendlyName: string;
  attributes: any;
  online: boolean;
  notifiable: boolean;
}

export interface UserServices {
  session: Session;
  syncClient: SyncClient;
}

namespace User {
  export type SubscriptionState = 'initializing' | 'subscribed' | 'unsubscribed';

  export type UpdateReason = 'friendlyName' | 'attributes' | 'reachabilityOnline' | 'reachabilityNotifiable';

  export interface UpdatedEventArgs {
    user: User;
    updateReasons: User.UpdateReason[];
  }
}

/**
 * @classdesc Extended user information.
 * Note that <code>isOnline</code> and <code>isNotifiable</code> properties are eligible
 * to use only if reachability function is enabled.
 * You may check if it is enabled by reading value of {@link Client}'s <code>reachabilityEnabled</code> property.
 *
 * @property {String} identity - User identity
 * @property {String} friendlyName - User friendly name, null if not set
 * @property {any} attributes - Object with custom attributes for user
 * @property {Boolean} isOnline - User real-time conversation connection status
 * @property {Boolean} isNotifiable - User push notification registration status
 * @property {Boolean} isSubscribed - Check if this user receives real-time status updates
 *
 * @fires User#updated
 * @fires User#userSubscribed
 * @fires User#userUnsubscribed
 *
 * @constructor
 * @param {String} identity - Identity of user
 * @param {String} entityId - id of user's object
 * @param {Object} datasync - datasync service
 * @param {Object} session - session service
 */
class User extends EventEmitter {

  private entity: any;
  private services: UserServices;
  private state: UserState;
  private promiseToFetch: Promise<User>;
  private subscribed: User.SubscriptionState;

  /**
   * The update reason for <code>updated</code> event emitted on User
   * @typedef {('friendlyName' | 'attributes' | 'reachabilityOnline' | 'reachabilityNotifiable')} User#UpdateReason
   */

  constructor(identity: string, entityName: string, services: UserServices) {
    super();

    this.subscribed = 'initializing';
    this.setMaxListeners(0);

    this.services = services;

    this.state = {
      identity: identity,
      entityName: entityName,
      friendlyName: null,
      attributes: {},
      online: null,
      notifiable: null
    };
  }

  public get identity(): string { return this.state.identity; }

  public set identity(identity: string) { this.state.identity = identity; }

  public set entityName(name: string) { this.state.entityName = name; }

  public get attributes() { return this.state.attributes; }

  public get friendlyName(): string { return this.state.friendlyName; }

  public get isOnline(): boolean { return this.state.online; }

  public get isNotifiable(): boolean { return this.state.notifiable; }

  public get isSubscribed(): boolean { return this.subscribed == 'subscribed'; }

  // Handles service updates
  _update(key: string, value: any) {
    let updateReasons: User.UpdateReason[] = [];
    log.debug('User for', this.state.identity, 'updated:', key, value);
    switch (key) {
      case 'friendlyName':
        if (this.state.friendlyName !== value.value) {
          updateReasons.push('friendlyName');
          this.state.friendlyName = value.value;
        }
        break;
      case 'attributes':
        const updateAttributes = parseAttributes(value.value, `Retrieved malformed attributes from the server for user: ${this.state.identity}`, log);
        if (!isDeepEqual(this.state.attributes, updateAttributes)) {
          this.state.attributes = updateAttributes;
          updateReasons.push('attributes');
        }
        break;
      case 'reachability':
        if (this.state.online !== value.online) {
          this.state.online = value.online;
          updateReasons.push('reachabilityOnline');
        }
        if (this.state.notifiable !== value.notifiable) {
          this.state.notifiable = value.notifiable;
          updateReasons.push('reachabilityNotifiable');
        }
        break;
      default:
        return;
    }
    if (updateReasons.length > 0) {
      this.emit('updated', { user: this, updateReasons: updateReasons });
    }
  }

  // Fetch reachability info
  _updateReachabilityInfo(map, update) {
    if (!this.services.session.reachabilityEnabled) {
      return Promise.resolve();
    }

    return map.get('reachability')
              .then(update)
              .catch(err => { log.warn('Failed to get reachability info for ', this.state.identity, err); });
  }

  // Fetch user
  async _fetch() {
    if (!this.state.entityName) {
      return this;
    }

    this.promiseToFetch = this.services.syncClient.map({ id: this.state.entityName, mode: 'open_existing', includeItems: true })
                              .then(map => {
                                this.entity = map;
                                map.on('itemUpdated', args => {
                                  log.debug(this.state.entityName + ' (' + this.state.identity + ') itemUpdated: ' + args.item.key);
                                  return this._update(args.item.key, args.item.data);
                                });
                                return Promise.all([
                                  map.get('friendlyName')
                                     .then(item => this._update(item.key, item.data)),
                                  map.get('attributes')
                                     .then(item => this._update(item.key, item.data)),
                                  this._updateReachabilityInfo(map,
                                    item => this._update(item.key, item.data))
                                ]);
                              })
                              .then(() => {
                                log.debug('Fetched for', this.identity);
                                this.subscribed = 'subscribed';
                                this.emit('userSubscribed', this);
                                return this;
                              })
                              .catch(err => {
                                this.promiseToFetch = null;
                                throw err;
                              });
    return this.promiseToFetch;
  }

  _ensureFetched() {
    return this.promiseToFetch || this._fetch();
  }

  /**
   * Updates user attributes.
   * @param {any} attributes new attributes for User.
   * @returns {Promise<User>}
   */
  @validateTypesAsync(['string', 'number', 'boolean', 'object', literal(null)])
  public async updateAttributes(attributes: any): Promise<User> {
    if (this.subscribed == 'unsubscribed') {
      throw new Error('Can\'t modify unsubscribed object');
    }

    await this.services.session.addCommand('editUserAttributes', {
      username: this.state.identity,
      attributes: JSON.stringify(attributes)
    });

    return this;
  }

  /**
   * Update Users friendlyName.
   * @param {String|null} friendlyName - Updated friendlyName
   * @returns {Promise<User>}
   */
  @validateTypesAsync(['string', literal(null)])
  public async updateFriendlyName(friendlyName: string | null): Promise<User> {
    if (this.subscribed == 'unsubscribed') {
      throw new Error('Can\'t modify unsubscribed object');
    }

    await this.services.session.addCommand('editUserFriendlyName', {
      username: this.state.identity,
      friendlyName: friendlyName
    });

    return this;
  }

  /**
   * Removes User from subscription list.
   * @returns {Promise<void>} Promise of completion
   */
  async unsubscribe(): Promise<void> {
    if (this.promiseToFetch) {
      await this.promiseToFetch;
      this.entity.close();
      this.promiseToFetch = null;
      this.subscribed = 'unsubscribed';
      this.emit('userUnsubscribed', this);
    }
  }
}

export { User };

/**
 * Fired when User's properties or reachability status have been updated.
 * @event User#updated
 * @type {Object}
 * @property {User} user - Updated User
 * @property {User#UpdateReason[]} updateReasons - Array of User's updated event reasons
 */
/**
 * Fired when Client is subscribed to User.
 * @event User#userSubscribed
 * @type {User}
 */
/**
 * Fired when Client is unsubscribed from this User.
 * @event User#userUnsubscribed
 * @type {User}
 */
